import apiClient from './apiClient';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * Prescription Templates API — CRUD for saved Rx presets
 *
 * Endpoints:
 *   GET    /api/organisation/prescription-templates              — List
 *   POST   /api/organisation/prescription-templates              — Create
 *   PUT    /api/organisation/prescription-templates/:id          — Update
 *   DELETE /api/organisation/prescription-templates/:id          — Delete
 *   POST   /api/organisation/prescription-templates/:id/apply    — Apply
 *
 * RX-TEMPLATES-BUILD: `category` ("SYMPTOMS"|"DIAGNOSIS"|"MEDICINES"|
 * "LAB_TESTS"|absent = whole prescription) and `symptoms[]` are additive
 * fields on the same row and pass straight through these calls — no new
 * parameter, no new endpoint. GET takes NO query parameters (search / category
 * filter / truncation are client-side over the one response), and
 * `max_allowed` is ALWAYS null: there is no cap, so no caller may default it.
 * ═══════════════════════════════════════════════════════════════════════
 */

const BASE = '/api/organisation/prescription-templates';

// ── List Templates ───────────────────────────────────────────────────

export const listPrescriptionTemplates = async () => {
  try {
    const response = await apiClient.get(BASE);
    return response.data;
  } catch (error) {
    console.error('[RxTemplateAPI] Error listing templates:', error);
    throw buildApiError(error);
  }
};

// ── Create Template ──────────────────────────────────────────────────

export const createPrescriptionTemplate = async (templateData) => {
  try {
    const response = await apiClient.post(BASE, templateData);
    return response.data;
  } catch (error) {
    console.error('[RxTemplateAPI] Error creating template:', error);
    throw buildApiError(error);
  }
};

// ── Update Template ──────────────────────────────────────────────────

export const updatePrescriptionTemplate = async (templateId, updates) => {
  try {
    const response = await apiClient.put(`${BASE}/${templateId}`, updates);
    return response.data;
  } catch (error) {
    console.error('[RxTemplateAPI] Error updating template:', error);
    throw buildApiError(error);
  }
};

// ── Delete Template ──────────────────────────────────────────────────

export const deletePrescriptionTemplate = async (templateId) => {
  try {
    const response = await apiClient.delete(`${BASE}/${templateId}`);
    return response.data;
  } catch (error) {
    console.error('[RxTemplateAPI] Error deleting template:', error);
    throw buildApiError(error);
  }
};

// ── Apply Template (increments usage count, returns template data) ───

export const applyPrescriptionTemplate = async (templateId) => {
  try {
    const response = await apiClient.post(`${BASE}/${templateId}/apply`);
    return response.data;
  } catch (error) {
    console.error('[RxTemplateAPI] Error applying template:', error);
    throw buildApiError(error);
  }
};

// ── Error helper ─────────────────────────────────────────────────────

function buildApiError(error) {
  const apiError = new Error(error.message || 'An unexpected error occurred');

  if (error.response?.status === 401) {
    apiError.type = 'UNAUTHORIZED';
    apiError.message = 'Session expired. Please login again.';
  } else if (error.response?.status === 403) {
    apiError.type = 'FORBIDDEN';
    apiError.message = error.response.data?.detail || 'You do not have permission for this action.';
  } else if (error.response?.status === 404) {
    apiError.type = 'NOT_FOUND';
    apiError.message = error.response.data?.detail || 'Template not found.';
  } else if (error.response?.status === 400) {
    apiError.type = 'BAD_REQUEST';
    apiError.message = error.response.data?.detail || 'Invalid request.';
  } else if (error.response?.status === 422) {
    apiError.type = 'VALIDATION';
    const detail = error.response.data?.detail;
    if (Array.isArray(detail)) {
      apiError.message = detail.map(d => d.msg || JSON.stringify(d)).join('; ');
    } else {
      apiError.message = typeof detail === 'string' ? detail : 'Validation error.';
    }
  } else if (error.code === 'ECONNABORTED' || !error.response) {
    apiError.type = 'NETWORK';
    apiError.message = 'Network error. Please check your connection.';
  } else {
    apiError.type = 'UNKNOWN';
    const rawDetail = error.response?.data?.detail;
    apiError.message = typeof rawDetail === 'string' ? rawDetail : (error.message || 'An unexpected error occurred');
  }

  return apiError;
}

const prescriptionTemplateApi = {
  listPrescriptionTemplates,
  createPrescriptionTemplate,
  updatePrescriptionTemplate,
  deletePrescriptionTemplate,
  applyPrescriptionTemplate,
};

export default prescriptionTemplateApi;
